/**
 * Stripe Payment Integration Component
 * 
 * Handles secure payment processing with Stripe Elements.
 * Provides card input, validation, and payment confirmation.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * Stripe payment configuration
 */
interface StripeConfig {
  publicKey: string;
  currency: string;
  country: string;
}

/**
 * Payment intent data
 */
interface PaymentIntent {
  id: string;
  clientSecret: string;
  amount: number;
  currency: string;
  status: 'requires_payment_method' | 'requires_confirmation' | 'succeeded' | 'failed';
}

/**
 * Stripe payment props
 */
interface StripePaymentProps {
  /** Payment amount in cents */
  amount: number;
  /** Currency code (e.g., 'usd') */
  currency?: string;
  /** Callback when payment succeeds */
  onSuccess: (paymentIntent: PaymentIntent) => void;
  /** Callback when payment fails */
  onError: (error: string) => void;
  /** Callback when payment is cancelled */
  onCancel?: () => void;
  /** Custom CSS classes */
  className?: string;
  /** Customer information */
  customerInfo?: {
    email: string;
    name: string;
    phone?: string;
  };
  /** Billing address */
  billingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

/**
 * Mock Stripe service for development
 * In production, this would be replaced with actual Stripe integration
 */
class MockStripeService {
  constructor(_config: StripeConfig) {
    // Configuration parameter prefixed with underscore to indicate intentional non-use
  }

  /**
   * Create payment intent
   */
  async createPaymentIntent(amount: number, currency: string): Promise<PaymentIntent> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      id: `pi_${Date.now()}`,
      clientSecret: `pi_${Date.now()}_secret_${Math.random().toString(36).substr(2, 9)}`,
      amount,
      currency,
      status: 'requires_payment_method'
    };
  }

  /**
   * Confirm payment
   */
  async confirmPayment(
    clientSecret: string,
    _paymentMethod: any,
    _billingDetails: any
  ): Promise<{ paymentIntent: PaymentIntent; error?: string }> {
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Simulate success/failure (90% success rate)
    const success = Math.random() > 0.1;
    
    if (success) {
      return {
        paymentIntent: {
          id: clientSecret.split('_')[1],
          clientSecret,
          amount: 0, // Would come from server
          currency: 'usd',
          status: 'succeeded'
        }
      };
    } else {
      return {
        paymentIntent: {
          id: clientSecret.split('_')[1],
          clientSecret,
          amount: 0,
          currency: 'usd',
          status: 'failed'
        },
        error: 'Your card was declined. Please try a different payment method.'
      };
    }
  }

  /**
   * Validate card details
   */
  validateCard(cardNumber: string, expiryDate: string, cvv: string): {
    valid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    // Card number validation (basic)
    if (!cardNumber.replace(/\s/g, '').match(/^\d{13,19}$/)) {
      errors.cardNumber = 'Invalid card number';
    }

    // Expiry date validation
    if (!expiryDate.match(/^\d{2}\/\d{2}$/)) {
      errors.expiryDate = 'Invalid expiry date (MM/YY)';
    } else {
      const [month, year] = expiryDate.split('/');
      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;
      
      if (parseInt(month) < 1 || parseInt(month) > 12) {
        errors.expiryDate = 'Invalid month';
      } else if (parseInt(year) < currentYear || 
                 (parseInt(year) === currentYear && parseInt(month) < currentMonth)) {
        errors.expiryDate = 'Card has expired';
      }
    }

    // CVV validation
    if (!cvv.match(/^\d{3,4}$/)) {
      errors.cvv = 'Invalid CVV';
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }
}

/**
 * Card input component
 */
interface CardInputProps {
  onCardChange: (card: {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
  }) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}

const CardInput: React.FC<CardInputProps> = ({
  onCardChange,
  errors,
  disabled = false,
}) => {
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  });

  const handleChange = (field: string, value: string) => {
    let formattedValue = value;
    
    // Format card number with spaces
    if (field === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
    
    // Format expiry date
    if (field === 'expiryDate') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').substr(0, 5);
    }
    
    // Format CVV (numbers only)
    if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '').substr(0, 4);
    }

    const updatedDetails = { ...cardDetails, [field]: formattedValue };
    setCardDetails(updatedDetails);
    onCardChange(updatedDetails);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Cardholder Name *
        </label>
        <input
          type="text"
          value={cardDetails.cardholderName}
          onChange={(e) => handleChange('cardholderName', e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.cardholderName ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100' : ''}`}
          placeholder="John Doe"
        />
        {errors.cardholderName && (
          <p className="text-red-600 text-xs mt-1">{errors.cardholderName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Card Number *
        </label>
        <input
          type="text"
          value={cardDetails.cardNumber}
          onChange={(e) => handleChange('cardNumber', e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.cardNumber ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100' : ''}`}
          placeholder="1234 5678 9012 3456"
          maxLength={19}
        />
        {errors.cardNumber && (
          <p className="text-red-600 text-xs mt-1">{errors.cardNumber}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiry Date *
          </label>
          <input
            type="text"
            value={cardDetails.expiryDate}
            onChange={(e) => handleChange('expiryDate', e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.expiryDate ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100' : ''}`}
            placeholder="MM/YY"
            maxLength={5}
          />
          {errors.expiryDate && (
            <p className="text-red-600 text-xs mt-1">{errors.expiryDate}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CVV *
          </label>
          <input
            type="text"
            value={cardDetails.cvv}
            onChange={(e) => handleChange('cvv', e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cvv ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100' : ''}`}
            placeholder="123"
            maxLength={4}
          />
          {errors.cvv && (
            <p className="text-red-600 text-xs mt-1">{errors.cvv}</p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main Stripe Payment Component
 */
export const StripePayment: React.FC<StripePaymentProps> = ({
  amount,
  currency = 'usd',
  onSuccess,
  onError,
  onCancel,
  className = '',
  customerInfo,
  billingAddress,
}) => {
  const [stripeService] = useState(() => new MockStripeService({
    publicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_demo',
    currency,
    country: 'US'
  }));

  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  /**
   * Initialize payment intent
   */
  useEffect(() => {
    const initializePayment = async () => {
      setIsLoading(true);
      try {
        const intent = await stripeService.createPaymentIntent(amount, currency);
        setPaymentIntent(intent);
      } catch (error) {
        onError('Failed to initialize payment');
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [amount, currency, stripeService, onError]);

  /**
   * Handle card details change
   */
  const handleCardChange = useCallback((card: typeof cardDetails) => {
    setCardDetails(card);
    setValidationErrors({}); // Clear errors on change
  }, []);

  /**
   * Handle payment submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentIntent) {
      onError('Payment not initialized');
      return;
    }

    // Validate card details
    const validation = stripeService.validateCard(
      cardDetails.cardNumber,
      cardDetails.expiryDate,
      cardDetails.cvv
    );

    // Check for missing cardholder name
    if (!cardDetails.cardholderName.trim()) {
      validation.errors.cardholderName = 'Cardholder name is required';
      validation.valid = false;
    }

    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    setIsProcessing(true);
    setValidationErrors({});

    try {
      // Create payment method object
      const mockPaymentMethod = {
        card: {
          number: cardDetails.cardNumber.replace(/\s/g, ''),
          exp_month: parseInt(cardDetails.expiryDate.split('/')[0]),
          exp_year: parseInt(`20${cardDetails.expiryDate.split('/')[1]}`),
          cvc: cardDetails.cvv,
        },
        billing_details: {
          name: cardDetails.cardholderName,
          email: customerInfo?.email,
          phone: customerInfo?.phone,
          address: billingAddress,
        },
      };

      // Confirm payment
      const result = await stripeService.confirmPayment(
        paymentIntent.clientSecret,
        mockPaymentMethod,
        mockPaymentMethod.billing_details
      );

      if (result.error) {
        onError(result.error);
      } else {
        onSuccess(result.paymentIntent);
      }
    } catch (error) {
      onError('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [paymentIntent, cardDetails, stripeService, customerInfo, billingAddress, onSuccess, onError]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <LoadingSpinner size="lg" />
        <p className="mt-3 text-gray-600">Initializing payment...</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Pay with Card
        </h3>
        
        {/* Payment Amount */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Amount to pay:</span>
            <span className="text-lg font-semibold text-gray-900">
              ${(amount / 100).toFixed(2)} {currency.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <CardInput
            onCardChange={handleCardChange}
            errors={validationErrors}
            disabled={isProcessing}
          />

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-900">Secure Payment</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Your payment information is encrypted and secure. We use industry-standard security measures.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4">
            {onCancel && (
              <Button
                type="button"
                onClick={onCancel}
                variant="outline"
                size="lg"
                disabled={isProcessing}
              >
                Cancel
              </Button>
            )}
            
            <Button
              type="submit"
              disabled={isProcessing}
              variant="primary"
              size="lg"
              className="ml-auto"
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  Processing Payment...
                </div>
              ) : (
                `Pay $${(amount / 100).toFixed(2)}`
              )}
            </Button>
          </div>
        </form>

        {/* Stripe Branding */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Powered by{' '}
            <span className="font-semibold text-blue-600">Stripe</span>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Stripe payment status component
 */
interface PaymentStatusProps {
  paymentIntent: PaymentIntent;
  onContinue: () => void;
  onRetry?: () => void;
}

export const PaymentStatus: React.FC<PaymentStatusProps> = ({
  paymentIntent,
  onContinue,
  onRetry,
}) => {
  const isSuccess = paymentIntent.status === 'succeeded';
  const isFailed = paymentIntent.status === 'failed';

  return (
    <div className="text-center space-y-4">
      {isSuccess && (
        <>
          <div className="text-green-600 text-6xl">✓</div>
          <h3 className="text-lg font-medium text-gray-900">Payment Successful!</h3>
          <p className="text-gray-600">
            Your payment of ${(paymentIntent.amount / 100).toFixed(2)} has been processed successfully.
          </p>
          <p className="text-sm text-gray-500">
            Payment ID: {paymentIntent.id}
          </p>
          <Button onClick={onContinue} variant="primary" size="lg">
            Continue
          </Button>
        </>
      )}

      {isFailed && (
        <>
          <div className="text-red-600 text-6xl">✗</div>
          <h3 className="text-lg font-medium text-gray-900">Payment Failed</h3>
          <p className="text-gray-600">
            We couldn't process your payment. Please try again or use a different payment method.
          </p>
          <div className="space-x-3">
            {onRetry && (
              <Button onClick={onRetry} variant="primary" size="lg">
                Try Again
              </Button>
            )}
            <Button onClick={onContinue} variant="outline" size="lg">
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default StripePayment;